import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, ConnectableObservable, Subscription } from 'rxjs';
import { map, first, publishReplay } from 'rxjs/operators';
import { Settings } from '../core/settings';
import { isNil, chain } from 'lodash';
import { UserStorage } from '../user/user-storage';
import { DlLink, DownloadLinkType } from '../documents/models/dlLink';
import { UserActionName } from './user-action';
import { ActivityData, ActivityDetailData, ActivityDocumentDetail, DetailedActivityData, DefaultActivityData, ActivitySiteDetail, ActivityStudySiteDetail } from './activity-data';
import { CategoryNaturalKey } from '../documents/models/category';
import { downloadActions } from './download-actions';
import { uploadActions } from './upload-actions';
import { NavigationActivity } from './navigation-activity/navigation-activity';
import { assertUnreachableCode } from '../shared/assert-unreachable-code';
import { EmailPreference } from '../user/email-preferences/email';
import { emailNotificationPreferencesActions } from './email-notification-preferences-actions';

export interface ActivitySession {
    userSessionId: string;
}

type UserEmulationActivityAction = 'StartEmulation' | 'EndEmulation';

@Injectable()
export class ActivityLogger implements OnDestroy {

    public get activitySession(): Observable<ActivitySession> {
        return this.activitySession$;
    }

    private readonly activitySession$: ConnectableObservable<ActivitySession>;
    private readonly activitySessionSubscription: Subscription;

    constructor(
        private readonly http: HttpClient,
        private readonly settings: Settings,
        private readonly userStorage: UserStorage,
        private readonly navigationActivity: NavigationActivity,
    ) {
        this.activitySession$ = this.createUserSession()
            .pipe(
                map(userSessionId => ({ userSessionId: userSessionId })),
                publishReplay(1),
            ) as ConnectableObservable<ActivitySession>;

        this.activitySessionSubscription = this.activitySession$.connect();
    }

    public ngOnDestroy(): void {
        this.activitySessionSubscription.unsubscribe();
    }

    public async logActivity(userActionName: UserActionName): Promise<void> {
        if (!userActionName) {
            return;
        }

        const activitySession = await this.activitySession$.pipe(first()).toPromise();

        await this.sendActivities([new DefaultActivityData(userActionName, activitySession.userSessionId)]);
    }

    public async logAccessEQueryDetailView(details: ActivitySiteDetail | ActivityStudySiteDetail): Promise<void> {
        if (!details) {
            return;
        }

        const activitySession = await this.activitySession$.pipe(first()).toPromise();

        await this.sendActivities([new DetailedActivityData('Access Query Detail View', activitySession.userSessionId, [details])]);
    }

    public async logDownloadActivity(dlLinks: DlLink | DlLink[]): Promise<void> {
        const links = !Array.isArray(dlLinks)
            ? [dlLinks]
            : dlLinks;

        const activitySession = await this.activitySession$.pipe(first()).toPromise();

        await this.processDownloadedLinks(activitySession, links);
    }

    public async logUploadActivity(categoryId: CategoryNaturalKey): Promise<void> {
        const userAction = uploadActions.uploadCategories[categoryId];

        if (!userAction) {
            return;
        }

        const activitySession = await this.activitySession$.pipe(first()).toPromise();
        const activityData = new DefaultActivityData(userAction, activitySession.userSessionId);

        await this.sendActivities([activityData]);
    }

    public logEditFileActivity(documentId: string): Promise<void>  {
        return this.processDocumentActivity('Edit File', documentId);
    }

    public logAddTranslationsActivity(documentId: string): Promise<void> {
        return this.processDocumentActivity('Add Translations', documentId);
    }

    public logHideUnhideDocumentActivity(documentId: string): Promise<void> {
        return this.processDocumentActivity('Hide/Unhide Document', documentId);
    }

    public logChangePreferredLanguageActivity(editedUserId: number): Promise<void> {
        return this.logUserSettingsActivity(editedUserId, ['Change Preferred Language']);
    }

    public logChangePreferredUnitsActivity(editedUserId: number): Promise<void> {
        return this.logUserSettingsActivity(editedUserId, ['Change Preferred Units']);
    }

    public logAddEmailNotificationActivity(editedUserId: number): Promise<void> {
        return this.logUserSettingsActivity(editedUserId, ['Add Email Notification']);
    }

    public logEditEmailNotificationActivity(editedUserId: number): Promise<void> {
        return this.logUserSettingsActivity(editedUserId, ['Edit Email Notification']);
    }

    public logDeleteEmailNotificationActivity(editedUserId: number): Promise<void> {
        return this.logUserSettingsActivity(editedUserId, ['Delete Email Notification']);
    }

    public logResetEmailPreferences(editedUserId: number): Promise<void> {
        return this.logUserSettingsActivity(editedUserId, ['Reset Email Preferences']);
    }

    public async logChangeEmailPreferencesActivity(emailPreferences: EmailPreference[], editedUserId: number): Promise<void> {
        if (!emailPreferences || !emailPreferences.length || !editedUserId) {
            return;
        }

        const userActions = chain(emailPreferences)
            .groupBy(x => x.categoryId)
            .map((grouped: EmailPreference[], categoryId: CategoryNaturalKey) => {
                const userAction = emailNotificationPreferencesActions[categoryId];
                return userAction ? userAction : null;
            })
            .filter(x => !!x)
            .value();

        await this.logUserSettingsActivity(editedUserId, userActions);
    }

    public async logNavigationActivity(): Promise<void> {
        const activityData = this.navigationActivity.getNavigationActivity();

        if (activityData) {
            await this.sendActivities([activityData]);
        }
    }

    public async logUserEmulationActivity(action: UserEmulationActivityAction, emulatedUserId: number): Promise<void> {
        let userAction: UserActionName = null;

        if (action === 'StartEmulation') {
            userAction = 'User Emulation started';
        } else if (action === 'EndEmulation') {
            userAction = 'User Emulation stopped';
        }

        if (!userAction) {
            return;
        }

        const activitySession = await this.activitySession$.pipe(first()).toPromise();

        return this.processUserEmulationActivity(
            activitySession,
            userAction,
            emulatedUserId);
    }

    public async refreshUserSession(): Promise<void> {
        const activitySession = await this.activitySession$.pipe(first()).toPromise();

        await this.post(`${this.settings.apiUrl}/activities/user-session/${activitySession.userSessionId}/refresh`).toPromise();
    }

    private async logUserSettingsActivity(editedUserId: number, userActionNames: UserActionName[]): Promise<void> {
        const currentUser = await this.userStorage.currentUser.pipe(first()).toPromise();

        if (!userActionNames
            || !userActionNames.length
            || !editedUserId
            || editedUserId === currentUser.metadata.userId) {
            return;
        }

        const activitySession = await this.activitySession$.pipe(first()).toPromise();
        const activityData = userActionNames.map(userActionName => new DefaultActivityData(userActionName, activitySession.userSessionId));

        await this.sendActivities(activityData);
    }

    private async processDocumentActivity(userActionName: UserActionName, documentId: string): Promise<void> {
        if (!userActionName || !documentId) {
            return;
        }

        const activitySession = await this.activitySession$.pipe(first()).toPromise();

        await this.sendActivities([
            new DetailedActivityData(
                userActionName,
                activitySession.userSessionId,
                [new ActivityDocumentDetail(documentId)]
            )
        ]);
    }

    private async processUserEmulationActivity(
        activitySession: ActivitySession,
        userActionName: UserActionName,
        emulatedUserId: number
    ) {
        if (!userActionName || isNil(emulatedUserId)) {
            return;
        }

        await this.sendActivities([new DefaultActivityData(userActionName, activitySession.userSessionId)]);
    }

    private async sendActivities(activityDataList: ActivityData[]) {
        try {
            await this
                .post(`${this.settings.apiUrl}/activities/log-activities`, activityDataList)
                .toPromise();
        } catch (error) {
            console.error(error);
        }
    }

    private createUserSession(): Observable<string> {
        return this
            .post<string>(`${this.settings.apiUrl}/activities/user-session`)
            .pipe(
                map(response => response)
            );
    }

    private post<T>(url: string, body: T = null): Observable<T> {
        const headers = new HttpHeaders().set('ignoreEmulation', 'ignoreEmulation');
        return this.http.post<T>(url, body, { headers });
    }

    private async processDownloadedLinks(activitySession: ActivitySession, links: DlLink[]): Promise<void> {
        if (!links || !links.length) {
            return;
        }

        const activityData = chain(links)
            .groupBy(link => link.categoryNaturalKey)
            .map((grouped: DlLink[], categoryId: CategoryNaturalKey) => {
                const userAction = downloadActions.documentCategories[categoryId];

                if (!userAction) {
                    return null;
                }

                const activityDetails = chain(grouped)
                    .map(link => this.getActvitiyDetailFromDlLink(link))
                    .filter(detail => !!detail)
                    .value();

                const activity = new DetailedActivityData(
                    userAction,
                    activitySession.userSessionId,
                    activityDetails
                );

                return activity;
            })
            .filter(activity => !!activity)
            .value();

        if (activityData.length) {
            await this.sendActivities(activityData);
        }
    }

    private getActvitiyDetailFromDlLink(link: DlLink): ActivityDetailData | null {
        switch (link.linkType) {
            case DownloadLinkType.ByFile:
                return new ActivityDocumentDetail(
                    link.reportId,
                );
            case DownloadLinkType.ByDate:
                return null;
            case DownloadLinkType.ByDocument:
                return new ActivityDocumentDetail(
                    link.documentId
                );
            default:
                assertUnreachableCode(link);
        }

        return null;
    }
}